import { useState } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  SwipeableDrawer,
  Typography as T,
} from "@mui/material";

import { FlexBoxRow } from "components/atoms";
import { getMountPoint, reduceIconPath } from "helpers/functions";
import { useStoreState } from "store/store";
import { palette } from "theme/palette";

import { SummaryWrapper } from "../summary/components/SummaryWrapper";

type Props = {
  backHandle: () => void;
  nextHandle: () => void;
  disableBackButton: boolean;
  nextButtonText?: string;
  plugForFirstStep: boolean;
  page6Variant: boolean;
  disableNextButton?: boolean;
};

const FormControlButtonsMobile = ({
  backHandle,
  nextHandle,
  disableBackButton,
  plugForFirstStep,
  page6Variant,
  nextButtonText = "Next",
  disableNextButton = false,
}: Props) => {
  // state
  const [isMobileTotalOpen, setMobileTotalOpen] = useState(false);
  const fee = useStoreState((s) => s.fee);
  const isTotalLoading = useStoreState((s) => s.isTotalLoading);

  const renderTotalPriceButton = () => (
    <ButtonBase
      onClick={setMobileTotalOpen.bind(null, true)}
      sx={{
        backgroundColor: palette.accent,
        color: palette.white,
        display: "flex",
        flexDirection: "row",
        zIndex: "999",
        height: "58px",
        width: "100px",
        fontSize: "12px",
        borderRadius: "8px",
      }}
    >
      {!isTotalLoading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <T variant="secondaryText" color={palette.white}>
            View Order
          </T>
          <T
            variant="cardLabelText"
            color={palette.white}
            sx={{
              marginTop: "1px",
              marginLeft: "2px",
            }}
          >
            $ {plugForFirstStep || !fee?.total ? "0.00" : fee.total}
          </T>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
          }}
        >
          <CircularProgress style={{ color: "rgba(255, 255, 255, .38)" }} />
        </Box>
      )}
    </ButtonBase>
  );

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: palette.white,
        position: "sticky",
        bottom: 0,
      }}
    >
      {/* Controls are clustered in the center so third-party chat launchers
          pinned to the bottom corners can't cover the back/next buttons. */}
      <FlexBoxRow
        styleProps={{
          padding: "16px",
          paddingLeft: "24px",
          paddingRight: "24px",
          justifyContent: "center",
          gap: "8px",
        }}
        notColumnOnMobile
      >
        <ButtonBase
          sx={{
            background: "none",
            width: 48,
            height: 48,
            borderRadius: "8px",
            borderWidth: "2px",
            borderStyle: "solid",
            borderColor: palette.grey,
            cursor: `${disableBackButton ? "default" : "pointer"}`,
          }}
          onClick={backHandle}
          disabled={disableBackButton}
        >
          {disableBackButton ? (
            <img src={reduceIconPath("back-button-icon-disabled.svg")} />
          ) : (
            <img src={reduceIconPath("back-button-icon.svg")} />
          )}
        </ButtonBase>
        {!page6Variant && renderTotalPriceButton()}
        <SwipeableDrawer
          anchor="bottom"
          open={isMobileTotalOpen}
          onClose={() => setMobileTotalOpen(false)}
          onOpen={() => setMobileTotalOpen(true)}
          hysteresis={0.2}
          ModalProps={{ container: getMountPoint() }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
            }}
          >
            <SummaryWrapper
              plugForFirstStep={plugForFirstStep}
              page6Variant={page6Variant}
              onClose={setMobileTotalOpen.bind(null, false)}
            />
          </Box>
        </SwipeableDrawer>
        <ButtonBase
          sx={{
            borderWidth: "2px",
            borderStyle: "solid",
            borderColor: disableNextButton ? palette.grey : palette.accent,
            background: disableNextButton ? palette.white : palette.accent,
            // Checkout spans the same center footprint as View Order + next.
            width: page6Variant ? 156 : 48,
            height: 48,
            borderRadius: "8px",
            justifyContent: "center",
            opacity: disableNextButton ? 0.6 : 1,
            cursor: disableNextButton ? "not-allowed" : "pointer",
          }}
          onClick={nextHandle}
          disabled={disableNextButton}
        >
          <T
            variant="h5sb"
            sx={{
              color: disableNextButton ? palette.black : palette.white,
              fontSize: [16, 18],
            }}
          >
            {nextButtonText}
          </T>
          {!page6Variant ? (
            <img
              src={reduceIconPath("next-button-icon.svg")}
              alt={"next button icon"}
            />
          ) : null}
        </ButtonBase>
      </FlexBoxRow>
    </Box>
  );
};

export default FormControlButtonsMobile;
